import React, { useCallback, useEffect, useState } from "react";
import api, { gstPortalClient, portalJob } from "@/lib/api";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { toast } from "sonner";
import { LogIn, RefreshCw, ShieldCheck, LogOut } from "lucide-react";

const errMsg = (e) => { const d = e?.response?.data?.detail; return typeof d === "string" ? d : d?.message || "gst.gov.in did not respond"; };
const img = (c) => (c?.startsWith("data:") ? c : `data:image/png;base64,${c}`);

export function PortalLogin({ onChange }) {
  const [st, setSt] = useState(null);
  const [sess, setSess] = useState(null);
  const [f, setF] = useState({ username: "", password: "", captcha: "", otp: "" });
  const [busy, setBusy] = useState(false);

  const refresh = useCallback(async () => {
    try { const r = await api.get("/gst-portal/status"); if (r.data.client_id) gstPortalClient.id = r.data.client_id; setSt(r.data); onChange?.(r.data.logged_in); if (r.data.logged_in) setSess(null); }
    catch (e) { setSt({ error: errMsg(e) }); }
  }, [onChange]);
  useEffect(() => { refresh(); }, [refresh]);

  const settle = async (out) => {
    let s = out;
    for (let i = 0; i < 40 && (s.status === "pending" || ["opening", "logging_in"].includes(s.state)); i++) {
      await new Promise((r) => setTimeout(r, 2500));
      s = (await api.get(`/portal-dl/sessions/${out.sid || sess?.sid}`)).data;
    }
    setSess(s);
    if (s.state === "ready") { toast.success("Logged in to gst.gov.in"); refresh(); }
    else if (s.message) toast.error(s.message, { duration: 8000 });
    return s;
  };
  const run = async (fn) => { setBusy(true); try { await settle((await fn()).data); } catch (e) { toast.error(errMsg(e), { duration: 8000 }); } setBusy(false); };

  const start = () => run(() => api.post("/portal-dl/sessions", { client_id: st.client_id }));
  const login = () => run(() => api.post(`/portal-dl/sessions/${sess.sid}/login`, { username: f.username, password: f.password, captcha: f.captcha }));
  const otp = () => run(() => api.post(`/portal-dl/sessions/${sess.sid}/otp`, { otp: f.otp }));
  const recap = () => run(() => api.post(`/portal-dl/sessions/${sess.sid}/refresh-captcha`));
  const logout = async () => { try { await api.delete(`/portal-dl/sessions/${st.session.sid}`); refresh(); } catch (e) { toast.error(errMsg(e)); } };

  if (!st) return null;
  if (st.error) return <div className="text-sm text-red-700" data-testid="portal-login-error">{st.error}</div>;
  if (st.logged_in) return (
    <div className="flex items-center gap-3 border border-emerald-300 bg-emerald-50 rounded-sm px-3 py-2 text-sm" data-testid="portal-logged-in">
      <ShieldCheck className="w-4 h-4 text-emerald-700" /> Logged in to gst.gov.in as <b>{st.session?.username}</b> · {st.gstin}
      <div className="flex-1" /><Button size="sm" variant="ghost" onClick={logout} className="gap-1" data-testid="portal-logout"><LogOut className="w-3.5 h-3.5" /> Log out</Button>
    </div>
  );
  return (
    <div className="border rounded-sm p-3 bg-muted/30 space-y-3" data-testid="portal-login">
      <div className="text-sm font-medium">gst.gov.in login · {st.gstin}</div>
      {!sess && <Button size="sm" onClick={start} disabled={busy} className="gap-1.5 bg-accent text-white" data-testid="portal-login-start"><LogIn className="w-3.5 h-3.5" /> {busy ? "Opening gst.gov.in…" : "Log in to GST portal"}</Button>}
      {sess && ["captcha", "error"].includes(sess.state) && (
        <div className="grid md:grid-cols-4 gap-2 items-end">
          <Input placeholder="GST User ID" value={f.username} onChange={(e) => setF({ ...f, username: e.target.value })} data-testid="portal-username" />
          <Input type="password" placeholder="Password" value={f.password} onChange={(e) => setF({ ...f, password: e.target.value })} data-testid="portal-password" />
          <div className="flex items-center gap-2">
            {sess.captcha && <img src={img(sess.captcha)} alt="captcha" className="h-9 border rounded-sm bg-white" data-testid="portal-captcha-img" />}
            <Button size="icon" variant="ghost" onClick={recap} disabled={busy} title="New captcha" data-testid="portal-captcha-refresh"><RefreshCw className="w-3.5 h-3.5" /></Button>
          </div>
          <div className="flex gap-2">
            <Input placeholder="Captcha" value={f.captcha} onChange={(e) => setF({ ...f, captcha: e.target.value })} data-testid="portal-captcha" />
            <Button onClick={login} disabled={busy} data-testid="portal-login-submit">{busy ? "…" : "Log in"}</Button>
          </div>
        </div>
      )}
      {sess?.state === "otp" && (
        <div className="flex gap-2 items-center">
          <span className="text-sm">gst.gov.in sent an OTP to the registered mobile / email</span>
          <Input className="w-32" maxLength={6} placeholder="OTP" value={f.otp} onChange={(e) => setF({ ...f, otp: e.target.value })} data-testid="portal-otp" />
          <Button onClick={otp} disabled={busy} data-testid="portal-otp-submit">{busy ? "…" : "Verify"}</Button>
        </div>
      )}
      {sess?.message && <div className="text-xs text-red-700" data-testid="portal-login-message">{sess.message}</div>}
      <div className="text-[11px] text-muted-foreground">The login runs on Zenith's server and stays connected for up to 7 days (gst.gov.in may end it earlier). Your password is sent only to gst.gov.in.</div>
    </div>
  );
}

export function EvcFile({ ret, period, disabled, onFiled }) {
  const [pan, setPan] = useState("");
  const [otp, setOtp] = useState("");
  const [sent, setSent] = useState(false);
  const [busy, setBusy] = useState(false);
  const base = `/gst-portal/${ret}/${period}`;
  const go = async (fn) => { setBusy(true); try { await fn(); } catch (e) { toast.error(errMsg(e), { duration: 9000 }); } setBusy(false); };
  return (
    <div className="border rounded-sm p-3 space-y-2" data-testid={`${ret}-evc`}>
      <div className="text-sm font-medium">File with EVC (OTP to the authorised signatory)</div>
      <div className="flex flex-wrap gap-2 items-center">
        <Input className="w-40 font-mono" maxLength={10} placeholder="Signatory PAN" value={pan} onChange={(e) => setPan(e.target.value.toUpperCase())} data-testid={`${ret}-pan`} />
        <Button size="sm" variant="outline" disabled={busy || disabled || pan.length !== 10}
          onClick={() => go(async () => { const r = await portalJob(`${base}/file-otp`, { pan }); setSent(true); toast.success(`OTP sent to ${r.data.signatory?.name || "the signatory"}`); })} data-testid={`${ret}-send-otp`}>Send OTP</Button>
        {sent && <>
          <Input className="w-32" maxLength={6} placeholder="EVC OTP" value={otp} onChange={(e) => setOtp(e.target.value)} data-testid={`${ret}-otp`} />
          <Button size="sm" className="bg-red-600 hover:bg-red-700 text-white" disabled={busy || otp.length !== 6}
            onClick={() => go(async () => {
              if (!window.confirm(`File ${ret.toUpperCase()} for ${period}? Filing cannot be undone.`)) return;
              const r = await portalJob(`${base}/file`, { pan, otp }); toast.success(`Filed · ARN ${r.data.arn || "(see portal)"}`, { duration: 12000 }); onFiled?.(r.data.arn);
            })} data-testid={`${ret}-file`}>File return</Button>
        </>}
      </div>
    </div>
  );
}
